import '@mdxeditor/editor/style.css';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import classnames from 'classnames';
import { createUseStyles } from 'react-jss';
import {
  MDXEditor, listsPlugin, headingsPlugin, toolbarPlugin, UndoRedo, ListsToggle, MDXEditorMethods,
} from '@mdxeditor/editor';
import { PrimaryButton } from '@fluentui/react';
import { INITIAL_MARKDOWN, MARKDOWN_PREFIX } from './common';
import { PlaygroundTheme } from '../../Theme';
import { useIsVivara } from '../../hooks/useIsVivara';

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  todoEditor: {
    '&.side-panel-mode': {
      position: 'absolute',
      top: 200,
      width: 300,
      left: 20,
      zIndex: 100,
    },

    '& h3': {
      fontWeight: 'normal',
    },
    '& .mdxeditor': {
      borderRadius: 5,
      overflow: 'hidden',
    },
    '& .mdxeditor-root-contenteditable': {
      background: 'var(--baseBg)',
    },
    '& .mdxeditor-toolbar': {
      borderRadius: 0,
    },
  },
  bottomBar: {},
  sendButton: {
    marginTop: 12,
    backgroundColor: theme.themePrimary,
    borderColor: theme.themePrimary,

    '&:hover': {
      backgroundColor: theme.themeDarkAlt,
      borderColor: theme.themeDarkAlt,
    },

    '&.is-vivara': {
      '&:hover': {
        backgroundColor: theme.themeLight,
        borderColor: theme.themeLight,
      },
      '&:active': {
        backgroundColor: theme.themeLighter,
        borderColor: theme.themeLighter,
      },
    },
  },
}));

export interface ITodoEditorProps {
  onSend(markdown: string): Promise<void>;
  sendButtonDisabled: boolean;
  conversationId: string;
  markdown?: string;
  onMarkdownChange?(markdown: string): void;
  isSidePanelMode?: boolean;
  className?: string;
}

function getTodoEditorWidth() {
  const contentContainer: HTMLDivElement | null = document.querySelector('.content-container');
  const screenWidth = document.body.offsetWidth;
  const CONTENT_PADDING = 12;
  const EDITOR_PADDING = 20;
  const todoEditorWidth = (screenWidth - contentContainer!.offsetWidth - CONTENT_PADDING * 2) / 2 - EDITOR_PADDING * 2;
  return todoEditorWidth;
}

export const TodoEditor: React.FC<ITodoEditorProps> = ({
  onSend,
  sendButtonDisabled,
  conversationId,
  markdown: propMarkdown,
  onMarkdownChange,
  isSidePanelMode,
  className,
}) => {
  const isVivara = useIsVivara();
  const classes = useStyles();
  const [markdown, setMarkdown] = useState(propMarkdown || INITIAL_MARKDOWN);
  const [editorWidth, setEditorWidth] = useState(0);
  const onSendButtonClick = useCallback(async () => {
    await onSend(`${MARKDOWN_PREFIX}${markdown}`);
  }, [markdown, onSend]);

  const resetEditorWidth = useCallback(() => {
    const todoEditorWidth = getTodoEditorWidth();
    setEditorWidth(todoEditorWidth);
  }, []);

  useEffect(() => {
    if (isSidePanelMode) {
      resetEditorWidth();
      window.addEventListener('resize', resetEditorWidth);

      return () => {
        window.removeEventListener('resize', resetEditorWidth);
      };
    }
    return () => {};
  }, [resetEditorWidth, isSidePanelMode]);

  const onMDEditorChange = useCallback((v: string) => {
    const processedValue = v.replace(/\\\*/g, '*').replace(/\\\[/g, '[');
    if (onMarkdownChange) {
      onMarkdownChange(processedValue);
    } else {
      setMarkdown(processedValue);
    }
  }, [onMarkdownChange]);
  const editorRef = useRef<MDXEditorMethods | null>();
  useEffect(() => {
    if (onMarkdownChange) {
      const finalContent = propMarkdown || INITIAL_MARKDOWN;
      setMarkdown(propMarkdown || INITIAL_MARKDOWN);
      editorRef.current?.setMarkdown(finalContent);
    }
  }, [propMarkdown, onMarkdownChange]);

  const convIdRef = useRef(conversationId);
  useEffect(() => {
    if (convIdRef.current !== conversationId && !onMarkdownChange) {
      convIdRef.current = conversationId;
      if (editorRef.current) {
        editorRef.current?.setMarkdown(INITIAL_MARKDOWN);
        setMarkdown(INITIAL_MARKDOWN);
      }
    }
  }, [conversationId, markdown, onMarkdownChange]);

  useEffect(() => {
    if (editorRef.current) {
      const editorInput = document.querySelector('.editor-input');
      editorInput?.addEventListener('keyup', (evt) => {
        if ((evt as KeyboardEvent).code === 'Tab') {
          // @ts-ignore
          const checklistToolbarBtn = window.$(document.querySelector('.mdxeditor-toolbar>div:first-child button'));
          console.log(checklistToolbarBtn);
          checklistToolbarBtn.click();
          evt.stopImmediatePropagation();
        }
      });
    }
  }, []);

  return (
    <div className={classnames(classes.todoEditor, isSidePanelMode && 'side-panel-mode', className)} style={isSidePanelMode ? { width: editorWidth, display: editorWidth === 0 ? 'none' : 'block' } : {}}>
      <MDXEditor
        // className="dark-theme dark-editor"
        contentEditableClassName="editor-input"
        ref={(editor) => {
          editorRef.current = editor;
        }}
        markdown={markdown}
        plugins={[
          listsPlugin(),
          headingsPlugin(),
          toolbarPlugin({
            // eslint-disable-next-line react/no-unstable-nested-components
            toolbarContents: () => (
              <>
                <ListsToggle options={['check']} />
                <UndoRedo />
              </>
            ),
          }),
        ]}
        onChange={onMDEditorChange}
      />
      <div className={classnames(classes.bottomBar, 'bottom-bar')}>
        <PrimaryButton className={classnames(classes.sendButton, isVivara && 'is-vivara')} disabled={sendButtonDisabled} onClick={onSendButtonClick}>Update</PrimaryButton>
      </div>
    </div>
  );
};
